import single_question from "@/assets/images/single_question.png";
import { ROUTES } from "@/constants/Routes";
import { ATTRIBUTION_NAMES } from "@/constants/User";
import { useUserStore } from "@/store/useUserStore";
import { hasAttribution } from "@/utils/permissions";
import { Alert, Box, Link, Typography } from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import Image from "next/image";

export function QuestionsManagement() {
  const attributions = useUserStore((s) => s.attributions) ?? [];
  const isAdmin = useUserStore((s) => s.isAdmin);
  const canRegisterAIAQuestionnaire =
    hasAttribution(attributions, ATTRIBUTION_NAMES.SOCIO_JURIDICO) || isAdmin;
  const canRegisterEPSQuestionnaire =
    hasAttribution(attributions, ATTRIBUTION_NAMES.ETICO) || isAdmin;

  return (
    <>
      <Alert severity="warning">
        Somente administradores e especialistas podem gerenciar as perguntas
      </Alert>

      <Typography variant="body2">
        Perguntas são um dos requisitos para que os questionários AIA e EPS
        possam ser cadastrados.
      </Typography>
      <Typography variant="body2">
        Para o{" "}
        {canRegisterAIAQuestionnaire ? (
          <Link href={ROUTES.QUESTIONNAIRE.REGISTER.AIA}>questionário AIA</Link>
        ) : (
          "questionário AIA"
        )}{" "}
        perguntas dos tipos <em>escolha única</em> e <em>múltipla escolha</em>{" "}
        devem estar cadastradas.
      </Typography>
      <Typography variant="body2">
        Para o{" "}
        {canRegisterEPSQuestionnaire ? (
          <Link href={ROUTES.QUESTIONNAIRE.REGISTER.EPS}>questionário EPS</Link>
        ) : (
          "questionário EPS"
        )}{" "}
        perguntas <em>abertas</em> devem estar cadastradas.
      </Typography>

      <Typography variant="body2">
        As perguntas podem ser{" "}
        {canRegisterAIAQuestionnaire || canRegisterEPSQuestionnaire ? (
          <>
            <Link href={ROUTES.QUESTION.REGISTER}>criadas</Link>,{" "}
            <Link href={ROUTES.QUESTION.EDIT}>editadas</Link>,{" "}
            <Link href={ROUTES.QUESTION.EDIT}>removidas</Link>
          </>
        ) : (
          "criadas, editadas e removidas"
        )}{" "}
        em suas respectivas páginas. Há uma separação de responsabilidades entre
        cada página para maior clareza dos objetivos das funcionalidades.
      </Typography>

      <Typography variant="body2">
        Alterar dados das perguntas não influencia nos questionários já
        cadastrados no sistema. Isto serve para manter consistência de dados e
        versões nos questionários publicadas e vinculados às aplicações das
        organizações.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box>
          <Typography variant="body2" color={blueGrey[700]} fontWeight={600}>
            Perguntas Objetivas
          </Typography>
          <Typography variant="body2" color={blueGrey[500]} fontSize={"0.8rem"}>
            Múltipla escolha e Escolha única
          </Typography>
        </Box>

        <Box>
          <Image
            src={single_question}
            alt="Exemplo pergunta de escolha única"
            style={{ borderRadius: "0.25rem" }}
            width={320}
          />
          <Typography
            component={"p"}
            variant="caption"
            color={blueGrey[500]}
            sx={{ marginTop: -0.2 }}
          >
            Exemplo de pergunta de escolha única
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2">
            As perguntas utilizadas para os questionários AIA são no formato
            objetivo para que o usuário selecione uma (escolha única) ou mais
            (múltiplas escolhas) alternativas.
          </Typography>

          <Box>
            <Typography variant="body2">
              Para a correta implementação e alteração das perguntas objetivas
              algumas regras devem ser observadas:
            </Typography>

            <ul
              style={{
                margin: 0,
                marginTop: 0.5,
                paddingLeft: "1.5rem",
                paddingTop: "0.2rem",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <li>
                <Typography variant="body2">
                  Toda pergunta possui um enunciado
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Toda pergunta possui duas ou mais alternativas
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Se a pergunta for <em>Múltipla escolha</em> então a soma da
                  pontuações das alternativas deve ser exatamente igual à 100
                </Typography>
              </li>
              <li>
                <Typography variant="body2">Para cada alternativa:</Typography>
                <ul
                  style={{
                    margin: 0,
                    marginTop: 0.5,
                    paddingLeft: "1.5rem",
                    paddingTop: "0.2rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  <li>
                    <Typography variant="body2" fontSize={"0.85rem"}>
                      Texto
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" fontSize={"0.85rem"}>
                      Pontuação
                    </Typography>
                  </li>
                </ul>
              </li>
            </ul>
          </Box>
        </Box>
      </Box>
    </>
  );
}
